import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutChangeEvent, StyleSheet } from 'react-native';
import Animated, {
    Easing,
    interpolateColor,
    useAnimatedProps,
    useAnimatedReaction,
    useAnimatedStyle,
    useDerivedValue,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import Svg from 'react-native-svg';
import { Defs, LinearGradient, Mask, Rect, Stop } from 'react-native-svg';

import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';

import { GRADIENT_COLORS, normalizeGradient } from './config';

const AnimatedText = Animated.createAnimatedComponent(Text);
const AnimatedRect = Animated.createAnimatedComponent(Rect);

const GRADIENT_POSITIONS = [0, 0.5768, 1.1426];

const ANIMATION_CONFIG = {
    duration: 500,
    easing: Easing.bezier(0.25, 0.1, 0.25, 1),
};

const BAR_HEIGHT = 4;
const BORDER_RADIUS = 2;
const SPACING = 6;
const CONTAINER_HEIGHT = 20;

export type FillMeterProps = {
    current: number;
    total: number;
    icon?: React.ReactNode;
};

/**
 * FillMeter
 *
 * Layout structure depends on state:
 *
 * When AT ZERO (current === 0):
 * [CURRENT_LABEL] - 6px - [EMPTY_BAR] - 6px - [MAX_LABEL]
 * (no left spacing, label is flush left)
 *
 * When NOT at max and NOT at zero (0 < current < total):
 * [FILLED_BAR] - 6px - [CURRENT_LABEL] - 6px - [EMPTY_BAR] - 6px - [MAX_LABEL]
 *
 * When AT MAX (current === total):
 * [FILLED_BAR] - 6px - [MAX_LABEL]
 * (current label is hidden)
 *
 */
export const FillMeter = memo(({ current, total, icon }: FillMeterProps) => {
    const [svgWidth, setSvgWidth] = useState(0);

    const containerWidth = useSharedValue(0);
    const currentLabelWidth = useSharedValue(0);
    const maxLabelWidth = useSharedValue(0);

    const safeTotal = Math.max(total, 1);
    const safeCurrent = Math.max(0, Math.min(current, safeTotal));
    const ratio = safeCurrent / safeTotal;
    const isAtMax = safeCurrent >= safeTotal;
    const isAtZero = ratio === 0;

    // Animated values
    const animatedRatio = useSharedValue(0);
    const animatedIsAtMax = useSharedValue(0);
    const animatedIsAtZero = useSharedValue(isAtZero ? 1 : 0);

    // Wrapper opacity (used to hide component until fully measured)
    const wrapperOpacity = useSharedValue(0);

    const isReady = useDerivedValue(() => {
        const needsCurrentLabel = animatedIsAtMax.value < 1;
        return (
            containerWidth.value > 0 && maxLabelWidth.value > 0 && (!needsCurrentLabel || currentLabelWidth.value > 0)
        );
    });

    // Fade in on the UI thread as soon as isReady flips to true
    useAnimatedReaction(
        () => isReady.value,
        (ready, wasReady) => {
            if (ready && !wasReady) {
                wrapperOpacity.value = withTiming(1, { duration: 100 });
            }
        }
    );

    const wrapperStyle = useAnimatedStyle(() => ({
        opacity: wrapperOpacity.value,
    }));

    // Animate values when current changes
    useEffect(() => {
        animatedRatio.value = withTiming(ratio, ANIMATION_CONFIG);
        animatedIsAtMax.value = withTiming(isAtMax ? 1 : 0, ANIMATION_CONFIG);
        animatedIsAtZero.value = withTiming(isAtZero ? 1 : 0, ANIMATION_CONFIG);
    }, [ratio, isAtMax, isAtZero, animatedRatio, animatedIsAtMax, animatedIsAtZero]);

    const {
        stops: normalizedStops,
        startX: gradientStartX,
        endX: gradientEndX,
    } = useMemo(() => normalizeGradient(GRADIENT_COLORS, GRADIENT_POSITIONS, svgWidth), [svgWidth]);

    // Left spacing: between filled bar and current label (0 at zero or at max)
    const animatedLeftSpacing = useDerivedValue(() => {
        return SPACING * (1 - animatedIsAtMax.value) * (1 - animatedIsAtZero.value);
    });

    // Right spacing: between current label and empty bar (0 at max)
    const animatedRightSpacing = useDerivedValue(() => {
        return SPACING * (1 - animatedIsAtMax.value);
    });

    const animatedCurrentLabelWidth = useDerivedValue(() => {
        return currentLabelWidth.value * (1 - animatedIsAtMax.value);
    });

    const animatedDrawableWidth = useDerivedValue(() => {
        return (
            containerWidth.value -
            animatedLeftSpacing.value -
            animatedCurrentLabelWidth.value -
            animatedRightSpacing.value -
            SPACING -
            maxLabelWidth.value
        );
    });

    // Animated filled bar width
    const animatedFilledBarProps = useAnimatedProps(() => {
        const filledWidth = animatedDrawableWidth.value * animatedRatio.value;
        return { width: Math.max(0, filledWidth) };
    });

    // `left` and `right` are set together so both edges update atomically
    // the right edge stays fixed next to the max label throughout the animation.
    const animatedEmptyBarStyle = useAnimatedStyle(() => {
        const emptyWidth = Math.max(0, animatedDrawableWidth.value * (1 - animatedRatio.value));
        const right = SPACING + maxLabelWidth.value;
        return {
            left: containerWidth.value - right - emptyWidth,
            right,
            opacity: 1 - animatedIsAtMax.value,
        };
    });

    // Animated current label position and opacity
    const animatedCurrentLabelStyle = useAnimatedStyle(() => {
        const filledWidth = animatedDrawableWidth.value * animatedRatio.value;
        return {
            left: filledWidth + animatedLeftSpacing.value,
            opacity: 1 - animatedIsAtMax.value,
        };
    });

    // Animated current label text color
    const animatedTextStyle = useAnimatedStyle(() => {
        // When value is zero, use text.secondary
        if (animatedRatio.value === 0) {
            return { color: ds.colorValue.text.secondary };
        }
        // The text needs to have the same color as the gradient at the current ratio
        // so we interpolate the color based on the same stops
        return {
            color: interpolateColor(
                animatedRatio.value,
                normalizedStops.map(stop => stop.offset),
                normalizedStops.map(stop => stop.color)
            ),
        };
    }, [normalizedStops]);

    // Max label is always right-aligned: left = containerWidth - maxLabelWidth
    const animatedMaxLabelStyle = useAnimatedStyle(() => ({
        left: containerWidth.value - maxLabelWidth.value,
    }));

    const onLayoutContainer = useCallback(
        (e: LayoutChangeEvent) => {
            const w = e.nativeEvent.layout.width;
            // Ignore zero-width events. They fire during navigation transitions
            if (w === 0) return;
            containerWidth.value = w;
            setSvgWidth(w);
        },
        [containerWidth]
    );

    const onLayoutCurrentLabel = useCallback(
        (e: LayoutChangeEvent) => {
            const w = e.nativeEvent.layout.width;
            // Ignore zero-width events. They fire during navigation transitions
            if (w === 0) return;
            currentLabelWidth.value = w;
        },
        [currentLabelWidth]
    );

    const onLayoutMaxLabel = useCallback(
        (e: LayoutChangeEvent) => {
            // Ignore zero-width events. They fire during navigation transitions
            const w = e.nativeEvent.layout.width;
            if (w === 0) return;
            maxLabelWidth.value = w;
        },
        [maxLabelWidth]
    );

    return (
        <Animated.View style={[styles.container, wrapperStyle]}>
            <Row style={styles.svgContainer} onLayout={onLayoutContainer}>
                {svgWidth > 0 && (
                    <>
                        <Svg height={BAR_HEIGHT} width={svgWidth}>
                            <Defs>
                                {/* Define the gradient across the entire drawable width */}
                                <LinearGradient
                                    id="fillGradient"
                                    x1={gradientStartX}
                                    y1={0}
                                    x2={gradientEndX}
                                    y2={0}
                                    gradientUnits="userSpaceOnUse"
                                >
                                    {normalizedStops.map(({ color, offset }, i) => (
                                        <Stop key={i} offset={offset} stopColor={color} />
                                    ))}
                                </LinearGradient>

                                {/* Mask that shows only the filled portion */}
                                <Mask id="fillMask">
                                    {/* White rectangle for filled area (animated) */}
                                    <AnimatedRect
                                        x={0}
                                        y={0}
                                        height={BAR_HEIGHT}
                                        fill={'white'}
                                        rx={BORDER_RADIUS}
                                        ry={BORDER_RADIUS}
                                        animatedProps={animatedFilledBarProps}
                                    />
                                </Mask>
                            </Defs>

                            {/* Full gradient bar (masked to show only filled portion) */}
                            <Rect
                                x={0}
                                y={0}
                                width={svgWidth}
                                height={BAR_HEIGHT}
                                fill="url(#fillGradient)"
                                rx={BORDER_RADIUS}
                                ry={BORDER_RADIUS}
                                mask="url(#fillMask)"
                            />
                        </Svg>

                        {/* Empty bar (animated) */}
                        <Animated.View style={[styles.emptyBar, animatedEmptyBarStyle]} />

                        {/* Current label (animated position, opacity, and color) */}
                        <AnimatedText
                            variant="titleMedium"
                            fontWeight={'500'}
                            style={[styles.label, animatedCurrentLabelStyle, animatedTextStyle]}
                            onLayout={onLayoutCurrentLabel}
                        >
                            {safeCurrent}
                        </AnimatedText>

                        {/* Max label (animated position) */}
                        <AnimatedText
                            variant="titleMedium"
                            fontWeight={'500'}
                            style={[styles.label, animatedMaxLabelStyle]}
                            color={
                                isAtMax ? ds.colorToken.background.accent.yellow.strong : ds.colorToken.text.secondary
                            }
                            onLayout={onLayoutMaxLabel}
                        >
                            {safeTotal}
                        </AnimatedText>
                    </>
                )}
            </Row>

            {icon}
        </Animated.View>
    );
});

const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    svgContainer: {
        flex: 1,
        height: CONTAINER_HEIGHT,
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyBar: {
        position: 'absolute',
        top: (CONTAINER_HEIGHT - BAR_HEIGHT) / 2,
        height: BAR_HEIGHT,
        borderRadius: BORDER_RADIUS,
        backgroundColor: ds.colorValue.background.neutral.strong,
    },
    label: {
        position: 'absolute',
        top: 0,
        lineHeight: CONTAINER_HEIGHT,
    },
});
